"use client";

import { useState } from "react";
import { Link2, Check, Copy, ExternalLink, Loader2, Image as ImageIcon, FileText } from "lucide-react";
import { Popover } from "@/components/ui/Popover";
import { parseDocIdentifier } from "@/lib/docIdentity";

/**
 * Public-share control for a single entity (issue | doc). Renders a 🔗 Share
 * trigger that opens a popover with one toggle — "Anyone with the link can
 * view". Flipping it POSTs /api/share/<type>/<id> { is_public }, and when
 * public surfaces the copyable public URL (origin + /p/<type>/<id>) with copy
 * + open-in-new-tab affordances.
 *
 * The public URL (`/p/<type>/<id>`) is the crawler-safe, unfurl-able link —
 * NOT the in-app `?doc=` / `?issue=` deep link. Replaces <CopyLinkButton> in
 * the doc/issue header toolbars (LITT-22 superseded by opt-in public sharing).
 */
export function ShareMenu({
  type,
  id,
  identifier,
  isPublic: initialPublic = false,
}: {
  type: "issue" | "doc";
  id: string;
  identifier?: string | null;
  isPublic?: boolean;
}) {
  const [isPublic, setIsPublic] = useState(initialPublic);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState<"link" | "identifier" | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const reference = type === "doc" ? parseDocIdentifier(identifier) : identifier?.trim() || null;

  async function downloadPng() {
    if (downloading || typeof window === "undefined") return;
    setDownloading(true);
    try {
      const res = await fetch(`/api/export/${type}/${id}/image`);
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `pauta-${type}-${id.slice(0, 8)}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
    } catch {
      /* network/permission failure — silent, the button just re-enables */
    } finally {
      setDownloading(false);
    }
  }

  const url =
    typeof window !== "undefined" ? `${window.location.origin}/p/${type}/${id}` : `/p/${type}/${id}`;

  async function toggle(next: boolean) {
    if (saving) return;
    setSaving(true);
    const prev = isPublic;
    setIsPublic(next); // optimistic
    try {
      const res = await fetch(`/api/share/${type}/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_public: next }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as { is_public: boolean };
      setIsPublic(data.is_public);
    } catch {
      setIsPublic(prev); // revert on failure
    } finally {
      setSaving(false);
    }
  }

  async function copy(value: string, target: "link" | "identifier") {
    if (typeof window === "undefined") return;
    setCopyError(null);
    try {
      await navigator.clipboard.writeText(value);
      setCopied(target);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setCopyError("Could not copy. Try again.");
    }
  }

  return (
    <Popover
      align="end"
      width={288}
      trigger={
        <button
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11.5px] font-medium text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] transition-colors"
          title="Share"
        >
          <Link2 size={13} />
          Share
        </button>
      }
    >
      {() => (
        <div className="p-3">
          {reference && (
            <div className="mb-3 flex items-center justify-between gap-2 border-b border-[var(--border)] pb-3">
              <span className="font-mono text-[12px] text-[var(--fg-secondary)]">{reference}</span>
              <button
                type="button"
                onClick={() => void copy(reference, "identifier")}
                aria-label={copied === "identifier" ? `${reference} copied` : `Copy ${reference}`}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] transition-colors"
              >
                {copied === "identifier" ? <Check size={13} /> : <Copy size={13} />}
                {copied === "identifier" ? "Copied" : "Copy ID"}
              </button>
            </div>
          )}
          {copyError && <p role="status" className="mb-2 text-[11.5px] text-[var(--fg-tertiary)]">{copyError}</p>}
          {/* Toggle row */}
          <label className="flex items-start gap-2.5 cursor-pointer select-none">
            <button
              type="button"
              role="switch"
              aria-checked={isPublic}
              disabled={saving}
              onClick={() => toggle(!isPublic)}
              className={
                "mt-0.5 relative h-[18px] w-[30px] shrink-0 rounded-full transition-colors " +
                (isPublic ? "bg-[var(--accent)]" : "bg-[var(--bg-active)]")
              }
            >
              <span
                className="absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-all"
                style={{ left: isPublic ? 14 : 2 }}
              />
            </button>
            <span className="flex-1" onClick={() => !saving && toggle(!isPublic)}>
              <span className="block text-[12.5px] text-[var(--fg)] leading-tight">
                Anyone with the link can view
              </span>
              <span className="block text-[11px] text-[var(--fg-tertiary)] mt-0.5 leading-snug">
                {isPublic
                  ? "Public — read-only, no sign-in required."
                  : "Private — only workspace members can see it."}
              </span>
            </span>
            {saving && (
              <Loader2 size={13} className="mt-1 animate-spin text-[var(--fg-tertiary)]" />
            )}
          </label>

          {/* Copyable URL — only when public */}
          {isPublic && (
            <div className="mt-3 flex items-center gap-1.5">
              <input
                readOnly
                value={url}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 min-w-0 bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-2 py-1 text-[11.5px] text-[var(--fg-secondary)] outline-none focus:border-[var(--accent)] truncate"
              />
              <button
                type="button"
                onClick={() => void copy(url, "link")}
                title={copied === "link" ? "Copied!" : "Copy link"}
                className="shrink-0 w-7 h-7 rounded-md flex items-center justify-center border border-[var(--border)] hover:bg-[var(--bg-hover)] text-[var(--fg-tertiary)] hover:text-[var(--fg)] transition-colors"
              >
                {copied === "link" ? <Check size={13} style={{ color: "var(--green)" }} /> : <Link2 size={13} />}
              </button>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title="Open public view"
                className="shrink-0 w-7 h-7 rounded-md flex items-center justify-center border border-[var(--border)] hover:bg-[var(--bg-hover)] text-[var(--fg-tertiary)] hover:text-[var(--fg)] transition-colors"
              >
                <ExternalLink size={13} />
              </a>
            </div>
          )}

          {/* Export */}
          <div className="mt-3 pt-3 border-t border-[var(--border)] flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wider text-[var(--fg-muted)] mb-0.5">
              Export
            </span>
            <button
              type="button"
              onClick={downloadPng}
              disabled={downloading}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[var(--bg-hover)] transition-colors text-[12.5px] text-[var(--fg-secondary)] text-left disabled:opacity-60"
            >
              {downloading ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <ImageIcon size={13} />
              )}
              Download image (PNG)
            </button>
            <a
              href={`/print/${type}/${id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[var(--bg-hover)] transition-colors text-[12.5px] text-[var(--fg-secondary)] text-left"
            >
              <FileText size={13} />
              Download PDF
            </a>
          </div>
        </div>
      )}
    </Popover>
  );
}
